import {
  IReferencePathNodeGeneral,
  IReferencePathNodeSpecific,
  IReferencePathReferenceDetails,
  IReferencePathReferenceDetailsDateTimePicker,
  IReferencePathReferenceDetailsNumber,
  IReferencePathReferenceDetailsPickList,
  IReferencePathReferenceDetailsText,
  IReferencePathServiceType,
  ReferencePathReferenceDetailsType,
  ServiceReferenceType
} from '~/interfaces/referencePathInterfaces'
import {Logger} from "~/services/logger";
import {ObjectHelper} from '~/helpers/objectHelper'
import {GenericType} from '~/interfaces/genericTypeInterfaces'
import {ModelType} from '~/enums/modelType'
import {DateHelper} from '~/helpers/dateHelper'
import {IOrganization} from '~/interfaces/organizationInterfaces'

/**
 * Handle logic related to ReferencePath that may be specific/dynamic, could involved multiple node, or generally something that shouldn't belong in the ReferencePath or ReferenceNode models
 */
export default abstract class ReferencePathService implements IReferencePathServiceType {
  abstract label: string
  abstract nodes: IReferencePathNodeSpecific[]
  abstract referenceGenericModelType: GenericType;
  abstract referenceModelType: ModelType;
  abstract imposterModelType: ModelType
  get imposterGenericModelType(): GenericType {
    return this.referenceGenericModelType
  }

  abstract reference(): ServiceReferenceType;

  buildReference(attrs: Record<string, any>): ServiceReferenceType {
    return this.reference()
  }

  findOrBuildReference(attrs: Record<string, any>): ServiceReferenceType {
    return this.reference() || this.buildReference(attrs)
  }

  get referenceExists(): boolean {
    return Boolean(this.reference())
  }

  abstract referenceIsCollection: boolean
  get referenceIsSingle(): boolean {
    return !this.referenceIsCollection
  }

  // reference can be appended/prepended to
  // override in subclass as needed
  get referenceIsAppendable(): boolean {
    return this.referenceIsCollection
  }

  // reference can be initialized
  // override in subclass as needed
  abstract referenceIsInitializable: boolean

  // reference can be selected/unselected (ex. checkboxes)
  // override in subclass as needed
  get referenceIsSelectable(): boolean {
    return false;
  }

  // reference can have a value removed
  // override in subclass as needed
  get referenceIsRemovable(): boolean {
    return false;
  }

  // reference can *not* be cleared
  // override in subclass as needed
  get referenceIsNotClearable(): boolean {
    return false;
  }

  // reference is a file (assumed is PDF)
  get referenceIsPdfCombinable(): boolean {
    return false
  }

  get referenceIsImageResizable(): boolean {
    return false
  }

  referenceIsPickList(): this is { referenceDetails: IReferencePathReferenceDetailsPickList } {
    return false
  }

  referenceIsDateTime(): this is { referenceDetails: IReferencePathReferenceDetailsDateTimePicker } {
    return this.referenceGenericModelType === ModelType.DateTime
  }

  referenceIsNumber(): this is { referenceDetails: IReferencePathReferenceDetailsNumber } {
    return this.referenceGenericModelType === ModelType.Number
  }

  referenceIsText(): this is { referenceDetails: IReferencePathReferenceDetailsText} {
    return this.referenceGenericModelType === ModelType.Text;
  }

  referenceIsFile() {
    return this.referenceGenericModelType === ModelType.File;
  }

  get breadcrumbs(): string[] {
    return [this.label]
  }

  abstract canLinkTo(node: IReferencePathNodeSpecific): boolean

  get referenceDetails(): IReferencePathReferenceDetails {
    return { type: ReferencePathReferenceDetailsType.Text }
  }

  protected referenceTypeError(process: string, node: IReferencePathNodeGeneral): void {
    Logger.errorOnce(process, `Unexpected :reference_type ${node.reference_type}.`);
  }

  protected referenceIdForTypeError(process: string, node: IReferencePathNodeGeneral): void {
    Logger.errorOnce(process, `Unexpected :reference_id ${node.reference_id} for :reference_type ${node.reference_type}.`);
  }

  protected debugCanLinkTo(className: string, myNode: IReferencePathNodeSpecific, otherNode: IReferencePathNodeSpecific | null, decision: boolean): void {
    const myData = ObjectHelper.sliceProperties(myNode, 'reference_type', 'reference_id')
    const otherData = ObjectHelper.sliceProperties(otherNode || ({} as any), 'reference_type', 'reference_id')
    Logger.debug('ReferencePathService#canLinkTo', className, 'Can link:', myData, 'to', otherData, ':', decision)
  }

  /**
   * override in inherited classes
   */
  toUniversalValue(value: any, ...args: any[]) {
    return value
  }

  /**
   * override in inherited classes
   */
  fromUniversalValue(value: any) {
    return value
  }

  /**
   * Call toUniversalValue works for most things, text fields, pick lists
   * but others like date times needs something a little different
   *
   * @param value
   * @param args
   */
  toDisplayValue(value: any, ...args: any[]) {
    if (this.referenceIsDateTime()) {
      const timezone: string = args[0]?.timezone || this.organization?.time_zone
      return DateHelper.toDisplay(value, { mode: this.referenceDetails.mode, timezone: timezone })
    } else {
      return this.toUniversalValue(value, ...args)
    }
  }

  /**
   * override in inherited classes where reference does have an organization
   * @protected
   */
  protected get organization(): IOrganization {
    return null
  }
}